import React, { useState, useEffect, useMemo } from 'react';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearSelectedClient } from '../../store/slices/selectedClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { BYPASS_LOGIN } from '../../utils/constants';

const LoginPage = () => {
  const { signIn, setActive } = useSignIn();
  const { isSignedIn, isLoaded } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectTarget = useMemo(() => {
    const from = location.state?.from;

    if (from?.pathname) {
      return {
        pathname: from.pathname,
        search: from.search || '',
        hash: from.hash || ''
      };
    }

    return { pathname: '/client' };
  }, [location.state]);

  // Redirigir a la página principal si ya está logeado
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      history.replace(redirectTarget);
    }
  }, [isLoaded, isSignedIn, history, redirectTarget]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Limpiar cualquier selectedClient de la sesión anterior
    dispatch(clearSelectedClient());

    try {
      const result = await signIn.create({
        identifier: email,
        password: password
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });

        // AuthWrapper will automatically fetch client data
        // Redirect to the originally requested route when available
        history.replace(redirectTarget);
      } else {
        setError('Unable to complete sign in. Please try again.');
      }
    } catch (err) {
      console.error('Error during sign in:', err);
      setError(err.errors?.[0]?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleBypass = () => {
    localStorage.setItem('devBypass', 'true');
    dispatch(clearSelectedClient());
    history.replace(redirectTarget);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      {/* Logo */}
      <div className="mb-8">
        <img
          src="../assets/img/adlerlocal_logo_2026.png"
          alt="Adler Local"
          className="h-10"
        />
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="mb-6 text-center">
            <h1 className="text-xl font-semibold tracking-tight">
              Welcome back
            </h1>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                id="email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#3766a8] hover:underline">
                  Forgot your password?
                </Link>
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full !bg-[#3766a8] hover:!bg-[#2d5286] !text-white !border-0"
              disabled={loading}>
              {loading ? 'Signing in...' : 'Login'}
            </Button>
          </form>

          {/* Dev Bypass */}

          {BYPASS_LOGIN === 'true' && (
            <div className="mt-4 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleBypass}>
                ⚡ Skip Login (Dev Mode)
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
